import { useEffect } from 'react';
import { ActivityType } from '../consts/types';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { RootState } from '../../../redux/store';
import { enqueueSnackbar } from 'notistack';
import { useGetEstablishmentActivitiesQuery } from '../../../services/companyApi';
import { setEstablihsmentActivityTypes } from '../../../redux/slices/establishmentSlice';

// CIIU4 classification rules:
// 5 digits (system strips leading 0) → Agro
// Starts with 41, 42, 43             → Construcción
// Starts with 1,2,3,5,6,7,8,9 or 44-49 → Industria o Administración

const AGRO_CIIU_V4_FIXED_LENGTH = 5; // system strips the leading 0, so a 5-char code means it originally started with 0
const CONSTRUCTION_CIIU_V4_STARTS = ['41', '42', '43'];
const IND_ADM_CIIU_V4_STARTS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

export const ACTIVITY_TYPE_AGRO_ID_V4 = 1;
export const ACTIVITY_TYPE_IND_ADM_ID_V4 = 2;
export const ACTIVITY_TYPE_CONSTRUCTION_ID_V4 = 3;

interface UseFindActivityTypeByCIIUV4 {
    getActivityTypeByCIIUV4: (ciiuCode: string) => ActivityType | null;
}

const useFindActivityTypeByCIIUV4 = (): UseFindActivityTypeByCIIUV4 => {
    const dispatch = useAppDispatch();

    const { data: establishmentActivities, isFetching } = useGetEstablishmentActivitiesQuery({});

    const establishmentActivityTypes =
        useAppSelector((state: RootState) => state.establishment.establishmentActivityTypes) ?? [];

    useEffect(() => {
        if (establishmentActivities?.data?.tiposActividad) {
            dispatch(setEstablihsmentActivityTypes(establishmentActivities.data.tiposActividad));
        }
    }, [establishmentActivities, dispatch]);

    const getActivityTypeByCIIUV4 = (ciiuCode: string): ActivityType | null => {
        if (!ciiuCode) return null;

        if (establishmentActivityTypes.length === 0) {
            if (!isFetching && !establishmentActivities?.data?.tiposActividad?.length) {
                enqueueSnackbar('No se pudieron cargar los tipos de actividad del establecimiento', {
                    variant: 'error'
                });
            }
            return null;
        }

        if (ciiuCode.length === AGRO_CIIU_V4_FIXED_LENGTH) {
            return (
                establishmentActivityTypes.find((type) => type.id === ACTIVITY_TYPE_AGRO_ID_V4) ??
                null
            );
        }

        // Must check construction before the generic '4' catch-all in IND_ADM_CIIU_V4_STARTS
        if (CONSTRUCTION_CIIU_V4_STARTS.some((start) => ciiuCode.startsWith(start))) {
            return (
                establishmentActivityTypes.find(
                    (type) => type.id === ACTIVITY_TYPE_CONSTRUCTION_ID_V4
                ) ?? null
            );
        }

        if (IND_ADM_CIIU_V4_STARTS.some((start) => ciiuCode.startsWith(start))) {
            return (
                establishmentActivityTypes.find(
                    (type) => type.id === ACTIVITY_TYPE_IND_ADM_ID_V4
                ) ?? null
            );
        }

        return null;
    };

    return { getActivityTypeByCIIUV4 };
};

export default useFindActivityTypeByCIIUV4;
